import { t, Trans } from '@lingui/macro';
import { Phone, Users } from 'lucide-react';
import { useReducer } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { My } from '../../services/My';
import { twil } from '../../services/TwilioHelper';
import { UserAvatar } from './UserAvatar';
import UserPopOver from './UserPopover';

const CallHeaderPreview: React.FC<{ room: Room, call: RoomCall }> = ({ room, call }) => {
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  if (!call || (twil.isConnected() && twil.getRoomId() == room.id) || (call && !RoomCall.isActive(call))) {
    return <></>
  } else
    return (
      <div className='mx-4 mb-3 flex items-center justify-between px-3 py-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 shadow-[0_0_15px_-3px_rgba(16,185,129,0.1)] animate-in fade-in slide-in-from-top-1 duration-300 backdrop-blur-sm'>
        <div className='flex items-center gap-3 overflow-hidden flex-1'>
          <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-700 shadow-sm shrink-0 select-none ring-1 ring-emerald-500/20">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <div className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 fill-current" />
              <span className="font-bold text-xs leading-none"><Trans>Call Requested</Trans></span>
            </div>
            <div className="w-px h-3 bg-emerald-500/20 mx-1" />
            <div className="flex items-center gap-1 text-xs font-bold tabular-nums leading-none">
              <Users className="w-3.5 h-3.5" />
              <span>{call.userIds.length}</span>
            </div>
          </div>

          <div className="flex -space-x-2 overflow-hidden hover:space-x-1 transition-all duration-300 pl-1">
            {call?.users.slice(0, 5).map(user => (
              <UserPopOver
                key={user.id}
                user={user}
                content={
                  <UserAvatar
                    src={user?.photoUrl}
                    name={user?.name}
                    className="w-8 h-8 ring-2 ring-background transition-transform hover:scale-110 hover:z-10 bg-background shadow-sm cursor-pointer"
                  />
                }
              />
            ))}
            {call.users.length > 5 && (
              <div className="w-8 h-8 rounded-[rgb(var(--radius-avatar))] bg-muted/80 flex items-center justify-center text-meta-sm font-medium ring-2 ring-background z-0 backdrop-blur-sm">
                +{call.users.length - 5}
              </div>
            )}
          </div>
        </div>

        <div className='ml-4'>
          <Button onClick={join} disabled={twil.isConnecting} size='sm' className="bg-emerald-600 hover:bg-emerald-700 text-on-primary-text font-bold rounded-full h-8 px-5 shadow-lg shadow-emerald-600/20 hover:shadow-emerald-600/30 transition-all">
            <Phone className="w-3.5 h-3.5 mr-2 fill-current" />
            <Trans id="participate">Join</Trans>
          </Button>
        </div>
      </div>
    );

  async function join() {
    forceUpdate();
    await twil.join(room, My.user.id);
  }
}

export default CallHeaderPreview;
